import {
  Alert,
  Box,
  Button,
  DatePicker,
  FormField,
  Modal,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import { useState, useEffect } from 'react';
import RequiredFieldLabel from '../RequiredFieldLabel';
import { formatDateTime } from 'src/utils/generalUtils';

const MIN_REASON_LENGTH = 10;
const MAX_REASON_LENGTH = 100;

interface ReopenTaskModalProps {
  visible: boolean;
  onDismiss: () => void;
  originalDueDate?: string;
  onSubmit?: (data: { newDueDate: string; reopenReason: string }) => void;
  isLoading?: boolean;
  maxReopens: number;
}

function ReopenTaskModal({
  visible,
  onDismiss,
  originalDueDate,
  onSubmit,
  isLoading = false,
  maxReopens,
}: ReopenTaskModalProps) {
  const [newDueDate, setNewDueDate] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (!visible) {
      setNewDueDate('');
      setReopenReason('');
      setValidationErrors({});
    }
  }, [visible]);

  const handleSubmit = () => {
    const errors: { [key: string]: string } = {};

    if (!newDueDate?.trim()) {
      errors.dueDate = 'New due date is required';
    } else {
      const [year, month, day] = newDueDate.split('-').map(Number);
      const selected = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selected <= today) {
        errors.dueDate = 'Please select a date in the future';
      }
    }

    if (!reopenReason?.trim()) {
      errors.reason = 'Reason for reopening is required';
    } else if (reopenReason.trim().length < MIN_REASON_LENGTH) {
      errors.reason = `Please provide a more detailed reason (at least ${MIN_REASON_LENGTH} characters)`;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});
    onSubmit?.({ newDueDate, reopenReason });
  };

  return (
    <Modal
      data-testid="reopen-task-modal"
      visible={visible}
      onDismiss={onDismiss}
      header="Reopen Task"
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} data-testid="cancel-button">
              Cancel
            </Button>
            <Button
              data-testid="reopen-task-button"
              variant="primary"
              onClick={handleSubmit}
              loading={isLoading}
            >
              Reopen Task
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <Alert type="warning" data-testid="reopen-warning-alert">
          Reopening this task will transition its status from <strong>Completed</strong>{' '}
          to <strong>In Progress</strong>. The original completion history will be
          preserved, and this action will be recorded in the task change log.
        </Alert>
        <Alert type="info">
          This task can only be reopened {maxReopens} time{maxReopens > 1 ? 's' : ''}.
          Once the limit is reached, it cannot be reopened further.
        </Alert>

        {originalDueDate && (
          <FormField
            label="Original Due Date at Completion"
            data-testid="original-due-date-field"
          >
            <Box color="text-status-inactive">
              {formatDateTime(originalDueDate, 'DD MMM YYYY')}
            </Box>
          </FormField>
        )}

        <FormField
          data-testid="new-due-date-field"
          label={<RequiredFieldLabel label="New Due Date" />}
          errorText={validationErrors.dueDate}
          constraintText="Must be a future date. The task will follow standard overdue logic if not completed by this date."
        >
          <DatePicker
            data-testid="new-due-date-picker"
            value={newDueDate}
            onChange={({ detail }) => {
              setNewDueDate(detail.value);
              if (validationErrors.dueDate) {
                setValidationErrors((prev) => ({ ...prev, dueDate: '' }));
              }
            }}
            isDateEnabled={(date) => date > new Date(new Date().setHours(0, 0, 0, 0))}
            placeholder="YYYY/MM/DD"
          />
        </FormField>

        <FormField
          data-testid="reopen-reason-field"
          label={<RequiredFieldLabel label="Reason for Reopening" />}
          errorText={validationErrors.reason}
          constraintText={`${reopenReason.length}/${MAX_REASON_LENGTH} characters`}
        >
          <Textarea
            data-testid="reopen-reason-textarea"
            value={reopenReason}
            onChange={({ detail }) => {
              const value = detail.value.slice(0, MAX_REASON_LENGTH);
              setReopenReason(value);
              if (validationErrors.reason) {
                setValidationErrors((prev) => ({ ...prev, reason: '' }));
              }
            }}
            placeholder="Describe why this task needs to be re-opened..."
            rows={3}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

export default ReopenTaskModal;
